// src/modules/Reports.tsx
import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { 
  BarChart3, PieChart, TrendingUp, TrendingDown, 
  Users, Calendar, CheckCircle, AlertCircle, FileText,
  Download, Filter, RefreshCw, Settings, ChevronDown,
  Clock, Activity, ClipboardList, Award
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────
const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB');
};

const getMonthName = (monthStr: string) => {
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const [year, month] = monthStr.split('-');
  return `${months[parseInt(month)-1]} ${year}`;
};

// ─── Component ─────────────────────────────────────────────
export function ReportsModule({ user }: { user: User }) {
  // ─── State ─────────────────────────────────────────────
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 10),
    end: new Date().toISOString().slice(0, 10),
  });
  const [activeTab, setActiveTab] = useState<'overview' | 'turnover' | 'audits' | 'incidents' | 'builder'>('overview');
  const [availableMetrics, setAvailableMetrics] = useState([
    'Residents Total', 'Admissions (Month)', 'Discharges (Month)',
    'Audits Completed', 'Audits Missed', 'Audit Compliance %',
    'Dining Audits Completed', 'Dining Audits Compliance %',
    'DoLS Active', 'DoLS Expired',
    'Staff Total', 'Training Records',
    'Low Stock Items', 'Action Plans Open',
  ]);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['Residents Total', 'Audits Completed', 'Low Stock Items']);
  const [metrics, setMetrics] = useState<any>({});
  const [loading, setLoading] = useState(false);

  // ─── Load data ──────────────────────────────────────────
  const loadData = () => {
    const residents = JSON.parse(localStorage.getItem('rmacs_residents') || '[]');
    const audits = JSON.parse(localStorage.getItem('rmacs_audits') || '[]');
    const diningAudits = JSON.parse(localStorage.getItem('rmacs_dining_audits') || '[]');
    const dols = JSON.parse(localStorage.getItem('rmacs_dols') || '[]');
    const staff = JSON.parse(localStorage.getItem('rmacs_staff') || '[]');
    const training = JSON.parse(localStorage.getItem('rmacs_training_completions') || '[]');
    const inventory = JSON.parse(localStorage.getItem('rmacs_inventory_stock') || '[]');
    const findings = JSON.parse(localStorage.getItem('rmacs_cqc_findings') || '[]');
    const actionPlans = JSON.parse(localStorage.getItem('rmacs_cqc_action_plans') || '[]');
    const policyReads = JSON.parse(localStorage.getItem('rmacs_policy_reads') || '[]');
    const invoices = JSON.parse(localStorage.getItem('rmacs_invoices') || '[]');

    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    start.setHours(0,0,0,0);
    end.setHours(23,59,59,999);

    const admittedInRange = residents.filter((r: any) => {
      const d = new Date(r.admission_date);
      return d >= start && d <= end;
    });
    const dischargedInRange = residents.filter((r: any) => {
      if (!r.exit_date) return false;
      const d = new Date(r.exit_date);
      return d >= start && d <= end;
    });

    const totalAudits = audits.length;
    const completedAudits = audits.filter((a: any) => a.answers && Object.values(a.answers).some((ans: any) => ans?.rating && ans.rating > 0));
    const auditCompliance = totalAudits > 0 ? (completedAudits.length / totalAudits) * 100 : 0;

    const totalDiningAudits = diningAudits.length;
    const completedDiningAudits = diningAudits.filter((a: any) => a.food_quality_rating && parseInt(a.food_quality_rating) > 0);
    const diningCompliance = totalDiningAudits > 0 ? (completedDiningAudits.length / totalDiningAudits) * 100 : 0;

    const activeDols = dols.filter((d: any) => d.current_status !== 'Expired' && d.current_status !== 'Refused' && d.current_status !== 'Withdrawn');
    const expiredDols = dols.filter((d: any) => d.current_status === 'Expired');

    const lowStock = inventory.filter((i: any) => i.current_stock < i.min_required);
    const openActionPlans = actionPlans.filter((p: any) => p.status === 'Open' || p.status === 'In Progress');

    return {
      residents,
      admittedInRange,
      dischargedInRange,
      totalAudits,
      completedAudits: completedAudits.length,
      auditCompliance,
      diningAudits: totalDiningAudits,
      diningCompliance,
      activeDols: activeDols.length,
      expiredDols: expiredDols.length,
      staff: staff.length,
      training: training.length,
      lowStock: lowStock.length,
      findings: findings.length,
      openActionPlans: openActionPlans.length,
      policyReads: policyReads.length,
      invoices: invoices.length,
    };
  };

  useEffect(() => {
    setLoading(true);
    const data = loadData();
    setMetrics(data);
    setLoading(false);
  }, [dateRange]);

  // ─── Export CSV ──────────────────────────────────────────
  const exportCSV = () => {
    const rows = [
      ['Metric', 'Value'],
      ['Total Residents', metrics.residents?.length || 0],
      ['Admissions (range)', metrics.admittedInRange?.length || 0],
      ['Discharges (range)', metrics.dischargedInRange?.length || 0],
      ['Audits Completed', metrics.completedAudits || 0],
      ['Audit Compliance %', metrics.auditCompliance?.toFixed(1) || 0],
      ['Dining Audits Completed', metrics.diningAudits || 0],
      ['Dining Compliance %', metrics.diningCompliance?.toFixed(1) || 0],
      ['Active DoLS', metrics.activeDols || 0],
      ['Expired DoLS', metrics.expiredDols || 0],
      ['Staff Members', metrics.staff || 0],
      ['Training Records', metrics.training || 0],
      ['Low Stock Items', metrics.lowStock || 0],
      ['Findings', metrics.findings || 0],
      ['Open Action Plans', metrics.openActionPlans || 0],
      ['Policy Reads', metrics.policyReads || 0],
      ['Invoices', metrics.invoices || 0],
    ];
    const csvContent = rows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Render helpers ──────────────────────────────────────
  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-4 text-center">
          <Users className="w-6 h-6 mx-auto text-[#123B36]" />
          <p className="text-2xl font-bold text-[#123B36]">{metrics.residents?.length || 0}</p>
          <p className="text-xs text-[#6B7A76]">Total Residents</p>
          <p className="text-xs text-green-600">Admitted: {metrics.admittedInRange?.length || 0}</p>
          <p className="text-xs text-red-600">Discharged: {metrics.dischargedInRange?.length || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-4 text-center">
          <ClipboardList className="w-6 h-6 mx-auto text-[#123B36]" />
          <p className="text-2xl font-bold text-[#123B36]">{metrics.completedAudits || 0}</p>
          <p className="text-xs text-[#6B7A76]">Audits Completed</p>
          <p className="text-xs text-[#6B7A76]">Compliance: {metrics.auditCompliance?.toFixed(1) || 0}%</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-4 text-center">
          <Award className="w-6 h-6 mx-auto text-[#123B36]" />
          <p className="text-2xl font-bold text-[#123B36]">{metrics.activeDols || 0}</p>
          <p className="text-xs text-[#6B7A76]">Active DoLS</p>
          <p className="text-xs text-red-600">Expired: {metrics.expiredDols || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-4 text-center">
          <AlertCircle className="w-6 h-6 mx-auto text-[#C22E2E]" />
          <p className="text-2xl font-bold text-[#C22E2E]">{metrics.lowStock || 0}</p>
          <p className="text-xs text-[#6B7A76]">Low Stock Alerts</p>
          <p className="text-xs text-[#6B7A76]">Open Actions: {metrics.openActionPlans || 0}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <h3 className="font-bold text-[#123B36] mb-3">Recent Activity</h3>
          <div className="space-y-2">
            <div className="flex justify-between border-b border-[#EDE8DB] py-1 text-sm">
              <span>New Admissions (range)</span>
              <span className="font-semibold">{metrics.admittedInRange?.length || 0}</span>
            </div>
            <div className="flex justify-between border-b border-[#EDE8DB] py-1 text-sm">
              <span>Discharges (range)</span>
              <span className="font-semibold">{metrics.dischargedInRange?.length || 0}</span>
            </div>
            <div className="flex justify-between border-b border-[#EDE8DB] py-1 text-sm">
              <span>Audits Completed</span>
              <span className="font-semibold">{metrics.completedAudits || 0}</span>
            </div>
            <div className="flex justify-between border-b border-[#EDE8DB] py-1 text-sm">
              <span>Dining Audits</span>
              <span className="font-semibold">{metrics.diningAudits || 0}</span>
            </div>
            <div className="flex justify-between border-b border-[#EDE8DB] py-1 text-sm">
              <span>Findings</span>
              <span className="font-semibold">{metrics.findings || 0}</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <h3 className="font-bold text-[#123B36] mb-3">Compliance Snapshot</h3>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-sm">
                <span>Audit Compliance</span>
                <span>{metrics.auditCompliance?.toFixed(1) || 0}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-[#123B36] h-2 rounded-full" style={{ width: `${Math.min(100, metrics.auditCompliance || 0)}%` }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm">
                <span>Dining Audit Compliance</span>
                <span>{metrics.diningCompliance?.toFixed(1) || 0}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-[#123B36] h-2 rounded-full" style={{ width: `${Math.min(100, metrics.diningCompliance || 0)}%` }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm">
                <span>Action Plans Open</span>
                <span>{metrics.openActionPlans || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTurnover = () => (
    <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
      <h3 className="text-lg font-bold text-[#123B36] mb-4">Resident Turnover</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#F7F5EE] rounded-lg p-4 text-center">
          <p className="text-sm text-[#6B7A76]">Current Residents</p>
          <p className="text-3xl font-bold text-[#123B36]">{metrics.residents?.length || 0}</p>
        </div>
        <div className="bg-[#E4F3EC] rounded-lg p-4 text-center">
          <p className="text-sm text-[#6B7A76]">Admitted (range)</p>
          <p className="text-3xl font-bold text-[#1E7F5C]">{metrics.admittedInRange?.length || 0}</p>
        </div>
        <div className="bg-[#FBE4E4] rounded-lg p-4 text-center">
          <p className="text-sm text-[#6B7A76]">Discharged (range)</p>
          <p className="text-3xl font-bold text-[#C22E2E]">{metrics.dischargedInRange?.length || 0}</p>
        </div>
      </div>
      <div className="mt-4">
        <h4 className="font-semibold text-[#123B36]">Admissions & Discharges (latest)</h4>
        <div className="mt-2 max-h-40 overflow-y-auto">
          {metrics.residents?.slice(0, 5).map((r: any) => (
            <div key={r.id} className="flex justify-between border-b border-[#EDE8DB] py-1 text-sm">
              <span>{r.first_name} {r.last_name}</span>
              <span className="text-[#6B7A76]">Admitted: {formatDate(r.admission_date)}</span>
              {r.exit_date && <span className="text-[#C22E2E]">Exited: {formatDate(r.exit_date)}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAudits = () => (
    <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
      <h3 className="text-lg font-bold text-[#123B36] mb-4">Audit Compliance</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-semibold">CQC Audits</h4>
          <div className="mt-2 space-y-2">
            <div className="flex justify-between border-b border-[#EDE8DB] py-1">
              <span>Total</span>
              <span>{metrics.totalAudits || 0}</span>
            </div>
            <div className="flex justify-between border-b border-[#EDE8DB] py-1">
              <span>Completed</span>
              <span>{metrics.completedAudits || 0}</span>
            </div>
            <div className="flex justify-between border-b border-[#EDE8DB] py-1">
              <span>Compliance</span>
              <span>{metrics.auditCompliance?.toFixed(1) || 0}%</span>
            </div>
          </div>
        </div>
        <div>
          <h4 className="font-semibold">Dining Audits</h4>
          <div className="mt-2 space-y-2">
            <div className="flex justify-between border-b border-[#EDE8DB] py-1">
              <span>Total</span>
              <span>{metrics.diningAudits || 0}</span>
            </div>
            <div className="flex justify-between border-b border-[#EDE8DB] py-1">
              <span>Completed</span>
              <span>{metrics.diningAudits || 0}</span>
            </div>
            <div className="flex justify-between border-b border-[#EDE8DB] py-1">
              <span>Compliance</span>
              <span>{metrics.diningCompliance?.toFixed(1) || 0}%</span>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <h4 className="font-semibold text-[#123B36]">DoLS Status</h4>
        <div className="flex gap-4 mt-1">
          <span className="text-sm">Active: <strong>{metrics.activeDols || 0}</strong></span>
          <span className="text-sm text-red-600">Expired: <strong>{metrics.expiredDols || 0}</strong></span>
        </div>
      </div>
    </div>
  );

  const renderIncidents = () => (
    <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
      <h3 className="text-lg font-bold text-[#123B36] mb-4">Incident Tracking</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#F7F5EE] rounded-lg p-4 text-center">
          <p className="text-sm text-[#6B7A76]">CQC Findings</p>
          <p className="text-2xl font-bold text-[#123B36]">{metrics.findings || 0}</p>
        </div>
        <div className="bg-[#FBEFD9] rounded-lg p-4 text-center">
          <p className="text-sm text-[#6B7A76]">Open Action Plans</p>
          <p className="text-2xl font-bold text-[#B8720B]">{metrics.openActionPlans || 0}</p>
        </div>
        <div className="bg-[#E4F3EC] rounded-lg p-4 text-center">
          <p className="text-sm text-[#6B7A76]">Policy Reads</p>
          <p className="text-2xl font-bold text-[#1E7F5C]">{metrics.policyReads || 0}</p>
        </div>
      </div>
      <div className="mt-4">
        <h4 className="font-semibold text-[#123B36]">Recent Findings</h4>
        <div className="mt-2 max-h-40 overflow-y-auto">
          {JSON.parse(localStorage.getItem('rmacs_cqc_findings') || '[]').slice(0, 3).map((f: any) => (
            <div key={f.id} className="border-b border-[#EDE8DB] py-2">
              <p className="font-medium">{f.inspector_name}</p>
              <p className="text-sm text-[#6B7A76]">{f.summary}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBuilder = () => (
    <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
      <h3 className="text-lg font-bold text-[#123B36] mb-4">Dynamic Report Builder</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-semibold">Select Metrics</h4>
          <div className="mt-2 space-y-1 max-h-60 overflow-y-auto">
            {availableMetrics.map(m => (
              <label key={m} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedMetrics.includes(m)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedMetrics([...selectedMetrics, m]);
                    } else {
                      setSelectedMetrics(selectedMetrics.filter(s => s !== m));
                    }
                  }}
                  className="accent-[#123B36]"
                />
                {m}
              </label>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-semibold">Report Preview</h4>
          <div className="mt-2 bg-[#F7F5EE] rounded-lg p-4 text-sm">
            {selectedMetrics.length === 0 ? (
              <p className="text-[#6B7A76]">Select metrics to preview.</p>
            ) : (
              <ul className="space-y-1">
                {selectedMetrics.map(m => {
                  let value: string | number = '—';  // ✅ Explicitly typed as string | number
                  switch(m) {
                    case 'Residents Total': value = metrics.residents?.length || 0; break;
                    case 'Admissions (Month)': value = metrics.admittedInRange?.length || 0; break;
                    case 'Discharges (Month)': value = metrics.dischargedInRange?.length || 0; break;
                    case 'Audits Completed': value = metrics.completedAudits || 0; break;
                    case 'Audits Missed': value = (metrics.totalAudits || 0) - (metrics.completedAudits || 0); break;
                    case 'Audit Compliance %': value = metrics.auditCompliance?.toFixed(1) + '%' || '0%'; break;
                    case 'Dining Audits Completed': value = metrics.diningAudits || 0; break;
                    case 'Dining Audits Compliance %': value = metrics.diningCompliance?.toFixed(1) + '%' || '0%'; break;
                    case 'DoLS Active': value = metrics.activeDols || 0; break;
                    case 'DoLS Expired': value = metrics.expiredDols || 0; break;
                    case 'Staff Total': value = metrics.staff || 0; break;
                    case 'Training Records': value = metrics.training || 0; break;
                    case 'Low Stock Items': value = metrics.lowStock || 0; break;
                    case 'Action Plans Open': value = metrics.openActionPlans || 0; break;
                    default: value = '—';
                  }
                  return (
                    <li key={m} className="flex justify-between border-b border-[#EDE8DB] py-1">
                      <span>{m}</span>
                      <span className="font-semibold">{value}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <button onClick={exportCSV} className="bg-[#123B36] text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header and Tabs */}
      <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 flex flex-wrap justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#123B36]">Reports & Analytics</h2>
          <p className="text-[#6B7A76]">System-wide performance and compliance insights</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setActiveTab('overview')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'overview' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>Overview</button>
          <button onClick={() => setActiveTab('turnover')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'turnover' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>Turnover</button>
          <button onClick={() => setActiveTab('audits')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'audits' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>Audits</button>
          <button onClick={() => setActiveTab('incidents')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'incidents' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>Incidents</button>
          <button onClick={() => setActiveTab('builder')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'builder' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>Report Builder</button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-2xl border border-[#E1DCCD] p-4 flex flex-wrap gap-4 items-center">
        <label className="text-sm font-medium text-[#20302C]">Date Range:</label>
        <input type="date" value={dateRange.start} onChange={(e) => setDateRange({...dateRange, start: e.target.value})} className="rounded-lg border border-[#D8D2C0] px-3 py-1 text-sm" />
        <span>to</span>
        <input type="date" value={dateRange.end} onChange={(e) => setDateRange({...dateRange, end: e.target.value})} className="rounded-lg border border-[#D8D2C0] px-3 py-1 text-sm" />
        <button onClick={() => { setDateRange({ start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0,10), end: new Date().toISOString().slice(0,10) }); }} className="text-sm text-[#123B36] underline">Last Month</button>
        <button onClick={() => { setDateRange({ start: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().slice(0,10), end: new Date().toISOString().slice(0,10) }); }} className="text-sm text-[#123B36] underline">Last Year</button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-12 text-[#6B7A76]">Loading data...</div>
      ) : (
        <>
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'turnover' && renderTurnover()}
          {activeTab === 'audits' && renderAudits()}
          {activeTab === 'incidents' && renderIncidents()}
          {activeTab === 'builder' && renderBuilder()}
        </>
      )}
    </div>
  );
}